"use client";

import { CheckCircle2 } from "lucide-react";
import { PlatformLogo } from "@/components/ui/platform-logo";
import { useJsonTranslation } from "@/hooks/useJsonTranslation";

const trustPointKeys = [
  "trust.points.0",
  "trust.points.1",
  "trust.points.2",
  "trust.points.3",
];

export default function AboutPage() {
  const { t } = useJsonTranslation("AboutPage");

  return (
    <div className="text-foreground container mx-auto px-4 py-12 pt-24 sm:py-16">
      <div className="mx-auto max-w-3xl">
        {/* Hero Section */}
        <header className="mb-16 text-center">
          <p className="text-muted-foreground text-sm tracking-widest uppercase">
            {t("hero.eyebrow")}
          </p>
          <h1 className="text-foreground font-display text-wrap-balance mt-4 text-4xl font-bold tracking-tight sm:text-5xl">
            {t("hero.title")}
          </h1>
          <p className="text-muted-foreground mt-4 text-lg">
            {t("hero.subtitle")}
          </p>
        </header>

        <div className="divide-border divide-y">
          {/* About Section */}
          <section className="py-12">
            <h2 className="text-foreground mb-6 text-xs font-semibold tracking-widest uppercase">
              {t("about.title")}
            </h2>
            <div className="text-foreground/80 space-y-4 text-lg leading-relaxed">
              <p className="text-foreground font-semibold">{t("about.lead")}</p>
              <p>{t("about.p1")}</p>
              <p>{t("about.p2")}</p>
              <p>{t("about.p3")}</p>
            </div>
          </section>

          {/* Team Section */}
          <section className="py-12">
            <h2 className="text-foreground mb-6 text-xs font-semibold tracking-widest uppercase">
              {t("team.title")}
            </h2>
            <div className="grid gap-8 md:grid-cols-[1.2fr_1fr]">
              <div className="text-foreground/80 space-y-4 text-lg leading-relaxed">
                <p className="text-foreground font-semibold">
                  {t("team.lead")}
                </p>
                <p>{t("team.p1")}</p>
              </div>

              <div className="space-y-4">
                <div className="glass rounded-2xl p-4">
                  <h3 className="text-foreground mb-1 font-semibold">
                    {t("team.volhaName")}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {t("team.members.volha")}
                  </p>
                </div>
                <div className="glass rounded-2xl p-4">
                  <h3 className="text-foreground mb-1 font-semibold">
                    {t("team.andreiName")}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {t("team.members.andrei")}
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Trust Section */}
          <section className="py-12">
            <h2 className="text-foreground mb-6 text-xs font-semibold tracking-widest uppercase">
              {t("trust.title")}
            </h2>
            <div className="space-y-6">
              <p className="text-foreground text-lg font-semibold">
                {t("trust.lead")}
              </p>
              <div className="grid gap-3 sm:grid-cols-2">
                {trustPointKeys.map((pointKey) => (
                  <div key={pointKey} className="flex items-start gap-3">
                    <CheckCircle2 className="text-leaf-400 mt-0.5 h-5 w-5 flex-shrink-0" />
                    <p className="text-foreground/80 text-sm leading-relaxed">
                      {t(pointKey)}
                    </p>
                  </div>
                ))}
              </div>
              <p className="text-foreground/80 text-lg leading-relaxed">
                {t("trust.p1")}
              </p>
            </div>
          </section>

          {/* Why Section */}
          <section className="py-12">
            <h2 className="text-foreground mb-6 text-xs font-semibold tracking-widest uppercase">
              {t("why.title")}
            </h2>
            <div className="space-y-4">
              <p className="text-foreground text-lg font-semibold">
                {t("why.lead")}
              </p>
              <p className="text-foreground/80 text-lg leading-relaxed">
                {t("why.p1")}
              </p>
              <blockquote className="border-leaf-500 border-l-4 pl-4">
                <p className="text-foreground font-semibold">
                  {t("why.highlight")}
                </p>
              </blockquote>
            </div>
          </section>

          {/* Social Section */}
          <section className="py-12">
            <h2 className="text-foreground mb-6 text-xs font-semibold tracking-widest uppercase">
              {t("social.title")}
            </h2>
            <a
              href="https://x.com/VidSeeds"
              target="_blank"
              rel="noopener noreferrer"
              className="border-border text-foreground/80 hover:text-foreground hover:bg-foreground/5 inline-flex items-center gap-3 rounded-xl border px-5 py-3 transition-colors"
            >
              <PlatformLogo platform="x" size={20} />
              <span className="font-semibold">{t("social.followOnX")}</span>
            </a>
          </section>
        </div>
      </div>
    </div>
  );
}
